import { makeStyles } from "@material-ui/core/styles";
import { useState } from "react";
import { changeAppState } from "../../../../../../../../common/appState";
import { fetchPost } from "../../../../../../../../common/util/fetch";
import ShurikenProgress from "../../../../../../../shared/Animations/ShurikenProgress";
import { PrimaryButton } from "../../../../../../../shared/Button/PrimaryButton";
import { CenterDialog } from "../../../../../../../shared/Dialog/CenterDialog";
import { TextField } from "../../../../../../../shared/Input/TextField";
import { BioLink, User } from "../../../../../../../shared/User/types";

export function AddLinksDialog({
    isAddLinkDialogOpen,
    closeDialog,
    user: { userId, bioLinks },
}: {
    isAddLinkDialogOpen: boolean;
    closeDialog: () => void;
    user: User;
}) {
    const c = useAddLinksDialogStyles();

    const [url, setUrl] = useState("");
    const [urlError, setUrlError] = useState("");

    const [title, setTitle] = useState("");
    const [titleError, setTitleError] = useState("");

    const isError = !!urlError || !!titleError;

    const [submitting, setSubmitting] = useState(false);

    return (
        <>
            <CenterDialog
                open={isAddLinkDialogOpen}
                onClose={() => {
                    closeDialog();
                }}
                className={c.addLinkDialog}
                transitionMilliseconds={200}
            >
                <div className="center">
                    <h2 className="bold">Add Social Link</h2>

                    <div className={c.inputContainer}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="URL"
                            autoComplete="url"
                            onChange={ev => {
                                const newUrl = ev.target.value
                                    .trim()
                                    .slice(0, 200);
                                setUrl(newUrl);
                                if (urlError) {
                                    setUrlError(getUrlError(newUrl, bioLinks));
                                }
                            }}
                            value={url}
                            placeholder={"https://example.com"}
                            onBlur={() => {
                                setUrlError(getUrlError(url, bioLinks));
                            }}
                            error={!!urlError}
                            helperText={
                                urlError && (
                                    <>
                                        <span className={c.errorText}>
                                            {urlError}
                                        </span>
                                        <br />
                                        <span className={c.errorText}>
                                            Example: https://example.com
                                        </span>
                                    </>
                                )
                            }
                        />

                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            label="Title"
                            onChange={ev => {
                                const newTitle = ev.target.value;
                                setTitle(newTitle);
                                setTitleError(getTitleError(newTitle));
                            }}
                            value={title}
                            className={c.titleField}
                            error={!!titleError}
                            helperText={
                                titleError && (
                                    <span className={c.errorText}>
                                        {titleError}
                                    </span>
                                )
                            }
                        />

                        <PrimaryButton
                            disabled={isError || submitting}
                            onClick={async () => {
                                setSubmitting(true);

                                const urlError = getUrlError(url, bioLinks);
                                if (urlError) {
                                    setUrlError(urlError);
                                    setSubmitting(false);
                                    return;
                                }
                                const titleError = getTitleError(title);
                                if (titleError) {
                                    setTitleError(titleError);
                                    setSubmitting(false);
                                    return;
                                }

                                // Save
                                await fetchUpdateBioLinks(userId, [
                                    ...bioLinks,
                                    { url, title },
                                ]);

                                closeDialog();

                                setTitle("");
                                setTitleError("");
                                setUrl("");
                                setUrlError("");
                                setSubmitting(false);
                            }}
                        >
                            {submitting ? (
                                <ShurikenProgress
                                    size={20}
                                    style={{ padding: 5 }}
                                />
                            ) : (
                                "Save"
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            </CenterDialog>
        </>
    );
}
const useAddLinksDialogStyles = makeStyles({
    addLinkDialog: {
        height: "auto",
        maxWidth: 320,
        maxHeight: "100%",
    },
    inputContainer: {
        padding: "0 10px 15px",
    },
    titleField: {
        marginTop: 5,
        marginBottom: 15,
    },
    errorText: {
        color: "red",
        fontSize: "small",
    },
});

function getUrlError(url: string, bioLinks: BioLink[]) {
    if (checkInvalidUrl(url)) {
        return "Please enter a valid URL.";
    }
    if (bioLinks.some(l => l.url === url.trim())) {
        return "You have already registered this URL.";
    }
    return "";
}

function checkInvalidUrl(url: string) {
    var regex =
        /(http|https):\/\/(\w+:{0,1}\w*)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
    if (!regex.test(url)) {
        return true;
    }

    try {
        new URL(url); // Just in case
    } catch {
        return true;
    }

    return false;
}

function getTitleError(title: string) {
    if (title.length > 20) {
        return "Title should be less than 20 characters.";
    }
    return "";
}

export async function fetchUpdateBioLinks(userId: number, bioLinks: BioLink[]) {
    const result = await fetchPost<
        { userId: number; bioLinks: BioLink[] },
        { user: User }
    >("api/User/UpdateBioLinks", { userId, bioLinks });
    if ("error" in result) {
        return;
    }
    changeAppState("user", result.user);
}
